import { Title, Group, Grid, Text } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import { PageWithBreadcrumbs } from '../../components/layout/PageWithBreadcrumbs';
import { ContainerWhite } from '../../components/core';
import AfaButton from '../../components/ui/AfaButton/AfaButton';
import { AfaCombobox } from '../../components/ui';
import { AfaTable } from '../../components/ui/AfaTable/AfaTable';
import { AfaDatePickerInput } from '../../components/ui/AfaDatePickerInput';
import { AfaMultiSelect } from '../../components/ui/AfaMultiSelect';
import { AfaTextInput } from '../../components/ui/AfaTextInput';
import { CancelTramesaModal } from '../../components/Modals/CancelTramesaModal/CancelTramesaModal';
import { useTramesesData } from '../../hooks/useTramesesData';
import { useTramesesFilter } from '../../hooks/useTramesesFilter';
import { getTramesesColumns } from '../../config/tramesesTableConfig';
import moment from 'moment';
import { DeleteTramesaModal } from '../../components/Modals/DeleteTramesaModal/DeleteTramesaModal';

// Página para consultar el estado de trámites existentes
export function TramesesConsultar(props:any) {
  const navigate = useNavigate();
  
  // Estados para todos los campos del formulario
  const [tipusTramit, setTipusTramit] = useState<string>('');
  const [subtipusTramit, setSubtipusTramit] = useState<string>('');
  const [dataDesDe, setDataDesDe] = useState<Date | null>(null);
  const [dataFinsA, setDataFinsA] = useState<Date | null>(null);
  const [filial, setFilial] = useState<string>('');
  const [usuariCreador, setUsuariCreador] = useState<string>('');
  const [periodicitat, setPeriodicitat] = useState<string[]>([]);
  const [estat, setEstat] = useState<string[]>([]);
  const [id, setId] = useState<string>('');
  
  // Estados para el modal de cancelación
  const [cancelModalOpened, setCancelModalOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [selectedTramesaId, setSelectedTramesaId] = useState<string>('');
  
  // Estado de loading para búsqueda
  const [searchLoading, setSearchLoading] = useState<boolean>(true);
  const { data: allTramesesData, loading, error, refetch } = useTramesesData(props.apiService, props.entity.lookupId, setSearchLoading);
  const { data: filters, loading: filtersLoading } = useTramesesFilter(props.apiService, props.entity.lookupId);

  // Estado para datos filtrados
  const [filteredData, setFilteredData] = useState(allTramesesData);

  // Per reiniciar la paginación a la página 1 cada vez que se apliquen nuevos filtros
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pagination = {
  currentPage: currentPage,
  onPageChange: setCurrentPage,
};
  
  // Sincronizar filteredData cuando cambien los datos originales
  useEffect(() => {
    //console.log(allTramesesData);
    setFilteredData(allTramesesData);
  }, [allTramesesData]);
  
  // Acción para iniciar un nuevo trámite
  const handleIniciarNou = () => {
    navigate('/trameses/iniciar-nova');
  };

  // Acción para borrar todos los filtros
  const handleEsborrarFiltres = () => {
    setTipusTramit('');
    setSubtipusTramit('');
    setDataDesDe(null);
    setDataFinsA(null);
    setFilial('');
    setUsuariCreador('');
    setPeriodicitat([]);
    setEstat([]);
    setId('');
    // Restaurar todos los datos sin filtrar
    setFilteredData(allTramesesData);
  };

  // Acción para aplicar los filtros
  const handleAplicarFiltres = async () => {
    setSearchLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      let filtered = [...allTramesesData];

    if (id) {
      filtered = filtered.filter(tramesa => 
        tramesa.guid.toLowerCase().includes(id.toLowerCase())
      );
    }

    // Filtrar por Estat (puede ser múltiple)
    if (estat.length > 0 && !estat.includes('Tots')) {
      filtered = filtered.filter(tramesa => 
        estat.includes(tramesa.state)
      );
    }

    // Filtrar por Tipus de tramit
    if (tipusTramit && tipusTramit !== 'Tots') {
      filtered = filtered.filter(tramesa => 
        tramesa.tramesaType.lookupId.toString().toLowerCase().includes(tipusTramit.toString())
      );
    }
    
    // Filtrar por rango de fechas
    if (dataDesDe || dataFinsA) {
      filtered = filtered.filter(tramesa => {
        const tramesaDate = moment(tramesa.registerDate).format('YYYY-MM-DD');

        const desde = dataDesDe ? moment(dataDesDe).format('YYYY-MM-DD') : null;
        const finsA = dataFinsA ? moment(dataFinsA).format('YYYY-MM-DD') : null;

        if (desde && finsA) {
          return moment(tramesaDate).isBetween(desde, finsA, undefined, '[]');
        } else if (desde) {
          return moment(tramesaDate).isSameOrAfter(desde);
        } else if (finsA) {
          return moment(tramesaDate).isSameOrBefore(finsA);
        }
        return true;
      });
    }
    
    // Filtrar por Filial
    if (filial && filial !== 'Tots') {
      filtered = filtered.filter(tramesa => 
        tramesa.filial.lookupValue.toLowerCase().includes(filial.toLowerCase())
      );
    }
    
    // Filtrar por Usuari creador
    if (usuariCreador && usuariCreador !== 'Tots') {
      filtered = filtered.filter(tramesa => 
        tramesa.createdBy.lookupValue.toLowerCase().includes(usuariCreador.toLowerCase())
      );
    }
    
    setFilteredData(filtered);
    } catch (error) {
      console.error('Error en la búsqueda de trameses:', error);
      // En el futuro, aquí podrías manejar errores de API
    } finally {
      setSearchLoading(false);
    }

    setCurrentPage(1); // Reiniciar a la primera página al aplicar filtros nuevos
  };

  // Handlers para la tabla
  const tableHandlers: any = {
    onCancelTramesa: (tramesaId: string) => {
      setSelectedTramesaId(tramesaId);
      setCancelModalOpened(true);
    },
    onDeleteDraft: (tramesaId: string) => {
      setSelectedTramesaId(tramesaId);
      setDeleteModalOpened(true);
    }
  };

  // Obtener columnas de la tabla
  const columns = getTramesesColumns(navigate, tableHandlers, props.user);

  // Verificar si hay filtros aplicados
  const hasActiveFilters = useMemo(() => {
    return (
      tipusTramit !== '' ||
      subtipusTramit !== '' ||
      dataDesDe !== null ||
      dataFinsA !== null ||
      filial !== '' ||
      usuariCreador !== '' ||
      periodicitat.length > 0 ||
      estat.length > 0 ||
      id !== ''
    );
  }, [tipusTramit, subtipusTramit, dataDesDe, dataFinsA, filial, usuariCreador, periodicitat, estat, id]);

  return (
    <PageWithBreadcrumbs>
      <div>
        <Title 
          order={1} 
          mb="sm"
        >
          Consultar Trameses
        </Title>
        
        <Group justify="space-between" align="flex-end">
          <Title 
            order={3} 
          >
            Filtres
          </Title>
          <AfaButton 
            id="btn-iniciar-nova"
            leftIcon={IconPlus}
            onClick={handleIniciarNou}
            variant="filled"
          >
            Iniciar nova
          </AfaButton>
        </Group>
      </div>

      {/* Contenedor principal para formularios, tablas, etc. */}
      <ContainerWhite>
        {/* Primera línea: Tipo de proceso y Proceso */}
        <Grid>
          <Grid.Col span={6}>
            <AfaTextInput
              label="ID"
              placeholder="Introdueix ID"
              value={id}
              onChange={(e) => setId(e.target.value)}
              name="id"
              id="id-tramesa"
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <AfaCombobox
              data={['Tots', ...filters.tipusTramit]}
              label="Tipologia de tramesa"
              placeholder="Tots"
              value={tipusTramit}
              onChange={setTipusTramit}
              name="tipusTramit"
              id="tipus-tràmit"
            />
          </Grid.Col>
          
        </Grid>

        {/* Segunda línea: Fecha de presentación desde, Hasta, Filial */}
        <Grid mt="md">
          <Grid.Col span={3}>
            <AfaDatePickerInput
              label="Data de presentació des de"
              placeholder="Selecciona data"
              value={dataDesDe}
              onChange={setDataDesDe}
              name="dataDesDe"
              id="data-des-de"
            />
          </Grid.Col>
          
          <Grid.Col span={3}>
            <AfaDatePickerInput
              label="Fins a"
              placeholder="Selecciona data"
              value={dataFinsA}
              onChange={setDataFinsA}
              name="dataFinsA"
              id="data-fins-a"
            />
          </Grid.Col>
          
          <Grid.Col span={6}>
            <AfaCombobox
              data={['Tots', ...filters.filial]}
              label="Filial"
              placeholder="Totes"
              value={filial}
              onChange={setFilial}
              name="filial"
              id="filial"
            />
          </Grid.Col>
        </Grid>

        {/* Tercera línea: Usuario creador y Estado */}
        <Grid mt="md">
          <Grid.Col span={6}>
            <AfaTextInput
              label="Creada per"
              placeholder="Creada per"
              value={usuariCreador}
              onChange={(e) => setUsuariCreador(e.target.value)}
              name="usuariCreador"
              id="usuari-creador"
            />
          </Grid.Col>
 
          <Grid.Col span={6}>
            <AfaMultiSelect
              data={[
                'Tramès',
                'Esborrany',
                'Finalitzat',
                'Pendent rebre documentació',
                'Cancel·lat entitat',
                'Rebutjat AFA',                
              ]}
              label="Estat"
              placeholder="Selecciona estats"
              value={estat}
              onChange={setEstat}
              name="estat"
              id="estat"
            />
          </Grid.Col>
        </Grid>

        {/* Cuarta línea: Botones de acción */}
        <Group justify="flex-end" gap={16} mt="lg">
          <AfaButton 
            id="btn-esborrar-filtres"
            variant="outline"
            onClick={handleEsborrarFiltres}
          >
            Esborrar filtres
          </AfaButton>
          <AfaButton 
            id="btn-cercar"
            variant="filled"
            onClick={handleAplicarFiltres}
          >
            Cercar
          </AfaButton>
        </Group>

        {/* Contador de resultados */}
        <Group gap={8} mt="md">
          <Text size="xl" fw={600}>{filteredData.length}</Text>
          <Text size="md"> {filteredData.length === 1 ? 'Resultat' : 'Resultats'}</Text>
        </Group>

        {/* Tabla de resultados */}
        <div style={{ marginTop: '12px' }} id="tramits-table-container">
          <AfaTable 
            columns={columns}
            data={filteredData}
            loading={searchLoading}
            loadingMessage="Cercant trameses..."
            emptyState={
              hasActiveFilters
                ? {
                    title: "No hi ha dades per mostrar.",
                    description: "Intenta ajustar els filtres o afegir nous criteris de cerca."
                  }
                : {
                    title: "No hi ha dades per mostrar."
                  }
            }
            pagination={pagination}
          />
        </div>

        {/* Alert de no hay resultados - ELIMINADO (ahora lo maneja AfaTable) */}

      </ContainerWhite>

      {/* Modal de cancelación */}
      <CancelTramesaModal
        apiService={props.apiService}
        opened={cancelModalOpened}
        onClose={() => setCancelModalOpened(false)}
        tramesaId={selectedTramesaId}
        entityId={props.entity.lookupId}
      />
      {/* Modal de cancelación */}
      <DeleteTramesaModal
        apiService={props.apiService}
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        tramesaId={selectedTramesaId}
      />
    </PageWithBreadcrumbs>
  );
}
